import React, { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import IKit_Builder_Option_Item from "../../../../../../../models/IKit_Builder_Option_Item";
import INewKit_Builder_Option_Item from "../../../../../../../models/INewKit_Builder_Option_Item";
import OwCheckbox from "../../../../../../shared/OwCheckbox/OwCheckbox";
import Typeahead from "../../../../../../shared/Typeahead/Typeahead";
import ICatalog_Item from "../../../../../../../models/ICatalog_Item";
import { v4 } from "uuid";
import UserContext from "../../../../../../../contexts/UserContext";
import SupplierContext from "../../../../../../../contexts/SupplierContext";
import { SocketContext } from "../../../../../../../contexts/SocketContext";
import IOnTypeaheadCatalogItemDTO from "../../../../../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import IUiSocketSetup from "../../../../../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../../../../../models/EndpointsEnum";
import ITypeaheadCatalogItemDTO from "../../../../../../../models/DTO/ITypeaheadCatalogItemDTO";
import IKit_Builder_Option_ItemUI from "../../../../../../../models/IKit_Builder_Option_ItemUI";
import OwLabel from "../../../../../../shared/OwLabel/OwLabel";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { OwAccordion } from "../../../../../../shared/OwAccordion/OwAccordion";
import OwToggle from "@components/shared/OwToggle/OwToggle";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CatalogItemKitBuilderSubItem } from "./CatalogItemKitBuilderSubItem/CatalogItemKitBuilderSubItem";

type Props = {
    catalogGuid: string;
    item: IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>;
    updateFn: (optionItem: IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>) => void;
    removeOptionItemFn: (optionItem: IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>) => void;
    id: string;
    isActive?: boolean;
    isDragOverlay?: boolean;
    openOverride?: boolean;
    accordionFn?: (item: IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>) => void;
};

export const CatalogItemKitBuilderOptionItem = (props: Props) => {
    // Sortable hook for drag and drop
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.id,
        data: {
            type: "kit-builder-option-item",
        },
    });

    // Apply styles based on drag state
    const style = props.isDragOverlay
        ? {
              position: "relative" as const,
              zIndex: 1000,
              opacity: 0.9,
          }
        : {
              transform: CSS.Transform.toString(transform),
              transition,
              opacity: isDragging ? 0.6 : 1,
              position: "relative" as const,
              zIndex: isDragging ? 1 : "auto",
          };

    // Styles for when active (being dragged)
    const activeStyles = props.isActive
        ? {
              background: "#f7f9fc",
              boxShadow: "0 0 10px rgba(0, 0, 0, 0.15)",
              border: "1px dashed #007bff",
              borderRadius: "4px",
              padding: "8px",
          }
        : {};

    // Style for drag overlay
    const overlayStyles = props.isDragOverlay
        ? {
              background: "#ffffff",
              boxShadow: "0 5px 16px rgba(0, 0, 0, 0.2)",
              opacity: 0.9,
              border: "1px solid #007bff",
              borderRadius: "4px",
              padding: "8px",
          }
        : {};

    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const socket = useContext(SocketContext)!;

    const getSubItems = (): IKit_Builder_Option_ItemUI[] => {
        const items = (props.item.Sub_Kit_Builder_Option_Items || [])
            .map((item, index) => {
                const uuid = v4();

                const updatedItem = {
                    ...item,
                    uuid: item.uuid || uuid,
                    sequence: item.sequence !== undefined ? item.sequence : index,
                } as IKit_Builder_Option_ItemUI;

                return updatedItem;
            })
            .sort((a, b) => {
                // Sort by sequence first
                if (a.sequence !== undefined && b.sequence !== undefined) {
                    return a.sequence - b.sequence;
                }
                // Fall back to id if no sequence
                if (a.id && b.id) {
                    return a.id - b.id;
                }
                return 0;
            });

        return items;
    };

    const [subItems, setSubItems] = useState<IKit_Builder_Option_ItemUI[]>(getSubItems());

    const [foundCatalogItems, setFoundCatalogItems] = useState<ICatalog_Item[]>([]);
    const [selectedSubItem, setSelectedSubItem] = useState<ICatalog_Item | undefined>();
    const [clearTypeahead, setClearTypeahead] = useState<boolean>(false);
    const [activeSubItemId, setActiveSubItemId] = useState<string | null>(null);
    const [activeSubItem, setActiveSubItem] = useState<IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI> | null>(null);

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // const hasMounted = useRef(false);

    const onTypeheadCatalogItems = (data: IOnTypeaheadCatalogItemDTO) => {
        console.log("Option Item onTypeaheadCatlaogItems: ", data.state);
        if (data.state === props.item.uuid) {
            setFoundCatalogItems(data.catalog_items);
        }
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onTypeheadCatalogItems }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        setSubItems(getSubItems());
    }, [props.item.Sub_Kit_Builder_Option_Items]);

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;

        console.log("handleChange: ", name, value);

        const updateItem = {
            ...props.item,
            [name]: value,
        };

        console.log("OptionItem handleChange: ", updateItem);
        // setItem(updateItem);
        // TODO Update the item in the parent component.
        props.updateFn(updateItem);
    };

    const getSubOptions = (name: string) => {
        // console.log("Options getSubOptions: ", uuid);
        const data: ITypeaheadCatalogItemDTO = {
            name,
            catalogGuid: props.catalogGuid,
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            state: props.item.uuid,
        };

        socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, data);
    };

    useEffect(() => {
        if (selectedSubItem) {
            const newKitOptionItem: Partial<IKit_Builder_Option_ItemUI> = {
                catalog_item_id: selectedSubItem.id,
                Catalog_Item: selectedSubItem,
                parent_option_item_id: props.item.id,
                uuid: v4(),
                sequence: 0,
            };

            const existingItems = props.item.Sub_Kit_Builder_Option_Items || [];
            const reorderedItems = existingItems.map((item, index) => ({
                ...item,
                sequence: index + 1,
            }));

            const updatedOptions: (IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>)[] = [
                newKitOptionItem,
                ...reorderedItems,
            ];

            const updateItem = {
                ...props.item,
                Sub_Kit_Builder_Option_Items: updatedOptions,
            };

            props.updateFn(updateItem);
            setSubItems(getSubItems());
            setSelectedSubItem(undefined);
            setClearTypeahead(true);
        }
    }, [selectedSubItem]);

    const selectSubOption = (optionItem: ICatalog_Item) => {
        setSelectedSubItem(optionItem);
    };

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
        setSelectedSubItem(undefined);
    };

    const removeSubOptionItem = (optionItem: IKit_Builder_Option_ItemUI | Partial<IKit_Builder_Option_ItemUI>) => {
        const optionItemIndex = subItems?.findIndex((item) => item.uuid === optionItem.uuid);

        if (optionItemIndex !== undefined && optionItemIndex !== -1) {
            let subKitOptionItems = [...subItems];

            if (optionItem.id) {
                subKitOptionItems[optionItemIndex].deleteFlag = !subKitOptionItems[optionItemIndex].deleteFlag;
            } else {
                subKitOptionItems = [...subKitOptionItems.slice(0, optionItemIndex), ...subKitOptionItems.slice(optionItemIndex + 1)];
            }

            const reorderedItems = subKitOptionItems.map((item, index) => ({
                ...item,
                sequence: index,
            }));

            const updatedOptionItem = {
                ...props.item,
                Sub_Kit_Builder_Option_Items: reorderedItems,
            };

            props.updateFn(updatedOptionItem);
            setSubItems(reorderedItems);
        }
    };

    const handleSubItemDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveSubItemId(active.id as string);

        const item = subItems.find((item) => item.uuid === active.id);
        if (item) {
            setActiveSubItem(item);
        }
    };

    const handleSubItemDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = subItems.findIndex((item) => item.uuid === active.id);
            const newIndex = subItems.findIndex((item) => item.uuid === over.id);

            const updatedArr = arrayMove(subItems, oldIndex, newIndex);

            const reorderedArr = updatedArr.map((item, index) => {
                return { ...item, sequence: index };
            });

            // Update local state first
            setSubItems(reorderedArr);

            // Then update parent after a brief delay to prevent accordion collapse
            setTimeout(() => {
                const updatedOptionItem = {
                    ...props.item,
                    Sub_Kit_Builder_Option_Items: reorderedArr,
                };
                props.updateFn(updatedOptionItem);
            }, 0);
        }

        setActiveSubItemId(null);
        setActiveSubItem(null);
    };

    const leftElemDetails = () => {
        return (
            <div className={"d-flex align-items-center"}>
                <div className="drag-handle me-2" {...listeners}>
                    <i className="fas fa-grip-lines" />
                </div>
                <i className={"fas fa-trash-alt text-danger me-2 pointer"} onClick={() => props.removeOptionItemFn(props.item)} />
                <span className={props.item.deleteFlag ? "text-strikethrough" : ""}>{props.item.Catalog_Item?.name}</span>
            </div>
        );
    };

    return (
        <div
            className={"CatalogItemKitBuilderOptionItem"}
            ref={setNodeRef}
            style={{ ...style, ...activeStyles, ...overlayStyles }}
            {...attributes}>
            <OwAccordion
                leftElem={leftElemDetails()}
                initOpen={false}
                chevronSide={"RIGHT"}
                openOverride={props.openOverride}
                clickFn={() => props.accordionFn?.(props.item)}>
                <div className={"ms-4"}>
                    <div className={"mb-2"}>
                        <OwToggle
                            label={"require_sub_option"}
                            name={"require_sub_option"}
                            active={props.item.require_sub_option || false}
                            fn={handleChange}
                            labelOnRight={true}
                        />
                    </div>
                    {props.item.require_sub_option ? (
                        <div className={"mt-2"}>
                            <div>
                                <OwLabel title={"sub_option_items"} />
                            </div>
                            <Typeahead
                                isSelectableFn={() => true}
                                typeaheadApiFn={getSubOptions}
                                titleProp={"name"}
                                subTitleProp={"sku"}
                                confirmAdd={true}
                                selectedItemFn={selectSubOption}
                                selectedItem={selectedSubItem}
                                typeaheadItems={foundCatalogItems}
                                clearTypeahead={clearTypeahead}
                                clearTypeaheadCallback={clearTypeaheadFn}
                                placeholder={"search"}
                            />

                            <div
                                className={"mt-2"}
                                onClick={(e) => e.stopPropagation()}
                                onPointerDown={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}>
                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragStart={handleSubItemDragStart}
                                    onDragEnd={handleSubItemDragEnd}>
                                    <SortableContext
                                        items={subItems.map((item) => item.uuid as string)}
                                        strategy={verticalListSortingStrategy}>
                                        {subItems?.map((kitOptionItem) => {
                                            return (
                                                <CatalogItemKitBuilderSubItem
                                                    key={kitOptionItem.uuid}
                                                    subItem={kitOptionItem}
                                                    removeSubItemFn={removeSubOptionItem}
                                                    id={kitOptionItem.uuid as string}
                                                    isActive={activeSubItemId === kitOptionItem.uuid}
                                                />
                                            );
                                        })}
                                    </SortableContext>
                                    <DragOverlay adjustScale={true}>
                                        {activeSubItem && (
                                            <div className="drag-overlay-sub-item">
                                                <CatalogItemKitBuilderSubItem
                                                    subItem={activeSubItem}
                                                    removeSubItemFn={() => {}}
                                                    id={activeSubItem.uuid as string}
                                                    isDragOverlay={true}
                                                />
                                            </div>
                                        )}
                                    </DragOverlay>
                                </DndContext>
                            </div>
                        </div>
                    ) : null}
                </div>
            </OwAccordion>
        </div>
    );
};
